/**
 * Person Service
 *
 * CRUD + merge operations for Person entities.
 * Backend: GET /api/v1/persons/  POST /api/v1/persons/{id}/merge/
 */

import { apiClient } from '@/shared/services/api';
import {
  type Person,
  type PersonFormData,
  type PersonPhone,
  type PersonPhoneFormData,
  type PersonEmail,
  type PersonEmailFormData,
  type CheckinMatchInput,
  type CheckinMatchCandidate,
  type CheckinMatchConfidence,
  type CheckinMatchReason,
  type CheckinMatchResult,
  type PersonStayHistory,
  joinName,
  mapBackendPerson,
} from '../types';

interface SearchPersonsFilters {
  search?: string;
  role?: Person['currentRole'];
  documentNumber?: string;
  hasUser?: boolean;
  /** Narrow to persons attached to one location (guests + staff there). */
  locationId?: string;
}

/**
 * Backend DateFields reject empty strings — treat empty/whitespace as null.
 * Kept narrow to date fields; generic string fields still send '' as blank.
 */
function nullableDate(v: string | null | undefined): string | null {
  const trimmed = typeof v === 'string' ? v.trim() : v;
  return trimmed ? trimmed : null;
}

// ── Search ─────────────────────────────────────────────────────────────────

export async function searchPersons(filters: SearchPersonsFilters = {}): Promise<Person[]> {
  const params: Record<string, string> = { page_size: '200' };
  if (filters.search?.trim()) params.search = filters.search.trim();
  if (filters.role) params.current_role = filters.role;
  if (filters.documentNumber?.trim()) params.document_number = filters.documentNumber.trim();
  if (typeof filters.hasUser === 'boolean') params.has_user = String(filters.hasUser);
  if (filters.locationId) params.location_id = filters.locationId;
  // Person is a tenant-scoped model; the wizard / admin search expects
  // tenant-wide results. Opt out of apiClient's automatic ?location_id=
  // injection so persons without a location attachment (no staff_profile,
  // no guest_profile, location_id=None) still appear. Callers that need
  // location-filtered results can pass `location_id` in `params`.
  const response = await apiClient.get<{ results: Record<string, unknown>[] }>(
    '/api/v1/persons/',
    { params, skipLocationFilter: true },
  );
  return response.data.results.map(mapBackendPerson);
}

export async function getPersonById(id: string): Promise<Person> {
  const response = await apiClient.get<Record<string, unknown>>(`/api/v1/persons/${id}/`, {
    skipLocationFilter: true,
  });
  return mapBackendPerson(response.data);
}

export async function getMyPerson(): Promise<Person> {
  const response = await apiClient.get<Record<string, unknown>>('/api/v1/persons/me/');
  return mapBackendPerson(response.data);
}

export async function createPerson(input: PersonFormData): Promise<Person> {
  const primaryEmail = (input.email ?? '').trim().toLowerCase();
  const payload: Record<string, unknown> = {
    name: joinName(input.firstName, input.lastName),
    first_name: input.firstName,
    last_name: input.lastName,
    display_name: joinName(input.firstName, input.lastName),
    email: primaryEmail,
    nationality: input.nationality ?? '',
    nationality_iso: input.nationalityIso ?? '',
    gender: input.gender ?? '',
    document_type: input.documentType ?? '',
    document_country_iso: input.documentCountryIso ?? '',
    document_number: input.documentNumber ?? '',
    document_expiration: nullableDate(input.documentExpiration),
    birth_date: nullableDate(input.birthDate),
    address: input.address ?? '',
    city: input.city ?? '',
    country: input.country ?? '',
    postal_code: input.postalCode ?? '',
    languages: input.languages ?? [],
    allergies: input.allergies ?? [],
    notes: input.notes ?? '',
  };
  if (input.locationId) {
    payload.location = input.locationId;
  }
  appendContactCollections(payload, input);

  const response = await apiClient.post<Record<string, unknown>>(
    '/api/v1/persons/',
    payload,
  );
  const person = mapBackendPerson(response.data);

  // Create phones via sub-endpoint
  const phones = input.phones ?? [];
  for (const phone of phones) {
    if (phone.phone.trim()) {
      await createPersonPhone(person.id, phone);
    }
  }

  // Create emails via sub-endpoint
  const emails = input.emails ?? [];
  for (const email of emails) {
    const normalizedEmail = email.email.trim().toLowerCase();
    if (normalizedEmail && normalizedEmail !== primaryEmail) {
      await createPersonEmail(person.id, email);
    }
  }

  return getPersonById(person.id);
}

export async function updatePerson(id: string, data: PersonFormData): Promise<Person> {
  const payload: Record<string, unknown> = {
    name: joinName(data.firstName, data.lastName),
    first_name: data.firstName,
    last_name: data.lastName,
    display_name: data.displayName ?? joinName(data.firstName, data.lastName),
    email: data.email ?? '',
    gender: data.gender ?? '',
    nationality: data.nationality ?? '',
    nationality_iso: data.nationalityIso ?? '',
    document_type: data.documentType ?? '',
    document_country_iso: data.documentCountryIso ?? '',
    document_number: data.documentNumber ?? '',
    document_expiration: nullableDate(data.documentExpiration),
    birth_date: nullableDate(data.birthDate),
    address: data.address ?? '',
    city: data.city ?? '',
    country: data.country ?? '',
    postal_code: data.postalCode ?? '',
    languages: data.languages ?? [],
    allergies: data.allergies ?? [],
    notes: data.notes ?? '',
  };
  appendContactCollections(payload, data);
  const response = await apiClient.patch<Record<string, unknown>>(`/api/v1/persons/${id}/`, payload);
  return mapBackendPerson(response.data);
}

/**
 * Emails/phones ride the person write itself (`emails_input`/`phones_input`,
 * PENDINGS #24): one atomic request replaces the old person → phones →
 * emails chain whose mid-chain failures left half-written profiles. Upsert
 * semantics — rows with `id` update in place, rows without are created, and
 * existing rows absent from the list are deleted. Keys only appended when
 * the caller provided the collection, so other write paths (which never
 * collect contacts) keep them untouched.
 */
function appendContactCollections(
  payload: Record<string, unknown>,
  data: PersonFormData | Partial<PersonFormData>,
) {
  if (data.emails) {
    payload.emails_input = data.emails.map((entry) => ({
      ...(entry.id ? { id: entry.id } : {}),
      email: entry.email,
      type: entry.type,
      is_primary: entry.isPrimary,
    }));
  }
  if (data.phones) {
    payload.phones_input = data.phones.map((entry) => ({
      ...(entry.id ? { id: entry.id } : {}),
      phone: entry.phone,
      phone_country_iso: entry.phoneCountryIso,
      phone_country_code: entry.phoneCountryCode,
      type: entry.type,
      is_primary: entry.isPrimary,
    }));
  }
}


// ── Check-in identity match ─────────────────────────────────────────────────

function mapStayHistory(raw: Record<string, unknown>): PersonStayHistory {
  const recent = Array.isArray(raw.recent) ? (raw.recent as Record<string, unknown>[]) : [];
  const staffRaw = (raw.staff as Record<string, unknown> | null) ?? null;
  return {
    pastStays: Number(raw.pastStays ?? 0),
    lastStayDate: (raw.lastStayDate as string) ?? null,
    lastRoom: (raw.lastRoom as string) ?? null,
    recent: recent.map((r) => ({
      arrivalDate: (r.arrivalDate as string) ?? null,
      departureDate: (r.departureDate as string) ?? null,
      room: (r.room as string) ?? null,
      bookingReference: (r.bookingReference as string) ?? null,
      status: (r.status as string) ?? '',
    })),
    staff: staffRaw
      ? {
          position: (staffRaw.position as string) ?? null,
          department: (staffRaw.department as string) ?? null,
          location: (staffRaw.location as string) ?? null,
          employmentType: (staffRaw.employmentType as string) ?? '',
          status: (staffRaw.status as string) ?? '',
          hireDate: (staffRaw.hireDate as string) ?? null,
          isActive: Boolean(staffRaw.isActive ?? false),
        }
      : null,
  };
}

/**
 * Front-desk identity recovery at check-in. Given a scanned wristband and/or
 * the details the guest provides, returns existing Person candidates (ranked
 * strong → possible) with a compact stay history, so staff can link to the
 * real person instead of creating a duplicate.
 */
export async function checkinMatch(input: CheckinMatchInput): Promise<CheckinMatchResult> {
  const response = await apiClient.post<{
    candidates?: Record<string, unknown>[];
    total?: number;
    overflow?: { reason: 'name'; count: number } | null;
  }>(
    '/api/v1/persons/checkin-match/',
    {
      nfc_uid: input.nfcUid,
      first_name: input.firstName,
      last_name: input.lastName,
      birth_date: nullableDate(input.birthDate),
      email: input.email,
      document_number: input.documentNumber,
    },
  );
  const candidates = (response.data.candidates ?? []).map((c) => ({
    person: mapBackendPerson((c.person as Record<string, unknown>) ?? {}),
    confidence: ((c.confidence as CheckinMatchConfidence) ?? 'possible'),
    matchReasons: ((c.matchReasons as CheckinMatchReason[]) ?? []),
    history: mapStayHistory((c.history as Record<string, unknown>) ?? {}),
  }));
  return {
    candidates,
    total: response.data.total ?? candidates.length,
    overflow: response.data.overflow ?? null,
  };
}

/**
 * Consolidate a fresh BL-minted booking person (source) into an existing
 * returning person (target) after staff confirm they're the same guest at
 * check-in. Moves the current booking's stay + wristband to the target and
 * deactivates the source. Destructive — gate behind a confirmation.
 */
export async function linkCheckin(targetId: string, sourcePersonId: string): Promise<Person> {
  const response = await apiClient.post<Record<string, unknown>>(
    `/api/v1/persons/${targetId}/link-checkin/`,
    { source_person_id: sourcePersonId },
  );
  return mapBackendPerson(response.data);
}

// ── Phone sub-resource ─────────────────────────────────────────────────────

function mapPhone(raw: Record<string, unknown>): PersonPhone {
  return {
    id: (raw.id as string) ?? '',
    phone: (raw.phone as string) ?? '',
    phoneCountryIso: (raw.phoneCountryIso as string) ?? '',
    phoneCountryCode: (raw.phoneCountryCode as string) ?? '',
    type: ((raw.type as string) ?? 'mobile') as PersonPhone['type'],
    isPrimary: Boolean((raw.isPrimary as boolean) ?? false),
    isActive: Boolean((raw.isActive as boolean) ?? true),
    createdAt: (raw.createdAt as string) ?? '',
    updatedAt: (raw.updatedAt as string) ?? '',
  };
}

export async function createPersonPhone(personId: string, data: PersonPhoneFormData): Promise<PersonPhone> {
  const response = await apiClient.post<Record<string, unknown>>(
    `/api/v1/persons/${personId}/phones/`,
    {
      phone: data.phone,
      phone_country_iso: data.phoneCountryIso,
      phone_country_code: data.phoneCountryCode,
      type: data.type,
      is_primary: data.isPrimary,
    },
  );
  return mapPhone(response.data);
}

export async function updatePersonPhone(personId: string, phoneId: string, data: Partial<PersonPhoneFormData>): Promise<PersonPhone> {
  const response = await apiClient.patch<Record<string, unknown>>(
    `/api/v1/persons/${personId}/phones/${phoneId}/`,
    {
      ...(data.phone !== undefined && { phone: data.phone }),
      ...(data.phoneCountryIso !== undefined && { phone_country_iso: data.phoneCountryIso }),
      ...(data.phoneCountryCode !== undefined && { phone_country_code: data.phoneCountryCode }),
      ...(data.type !== undefined && { type: data.type }),
      ...(data.isPrimary !== undefined && { is_primary: data.isPrimary }),
    },
  );
  return mapPhone(response.data);
}

export async function deletePersonPhone(personId: string, phoneId: string): Promise<void> {
  await apiClient.delete(`/api/v1/persons/${personId}/phones/${phoneId}/`);
}

/**
 * Strip repeated leading dial codes like "+34+34..." that can appear when a
 * user re-submits a form pre-filled with an already-prefixed number. We only
 * collapse EXACT duplicates of the provided dialCode; partial overlaps are
 * left alone.
 */
function stripDuplicateDialPrefix(phone: string, dialCode: string): string {
  if (!dialCode) return phone;
  let out = phone.trim();
  while (out.startsWith(dialCode + dialCode)) {
    out = out.slice(dialCode.length);
  }
  return out;
}

export async function syncPersonPrimaryPhone(personId: string, phone: string): Promise<void> {
  const person = await getPersonById(personId);
  const currentPrimaryPhone = person.primaryPhone ?? person.phones.find((entry) => entry.isActive) ?? null;
  const dialCode = currentPrimaryPhone?.phoneCountryCode ?? '';
  const normalizedPhone = stripDuplicateDialPrefix(phone.trim(), dialCode);

  if (!normalizedPhone) {
    if (currentPrimaryPhone?.id) {
      await deletePersonPhone(personId, currentPrimaryPhone.id);
    }
    return;
  }

  const payload: PersonPhoneFormData = {
    phone: normalizedPhone,
    phoneCountryIso: currentPrimaryPhone?.phoneCountryIso ?? '',
    phoneCountryCode: dialCode,
    type: currentPrimaryPhone?.type ?? 'mobile',
    isPrimary: true,
  };

  if (currentPrimaryPhone?.id) {
    await updatePersonPhone(personId, currentPrimaryPhone.id, payload);
    return;
  }

  await createPersonPhone(personId, payload);
}

// ── Email sub-resource ─────────────────────────────────────────────────────

function mapEmail(raw: Record<string, unknown>): PersonEmail {
  return {
    id: (raw.id as string) ?? '',
    email: (raw.email as string) ?? '',
    type: ((raw.type as string) ?? 'personal') as PersonEmail['type'],
    isPrimary: Boolean((raw.isPrimary as boolean) ?? false),
    isVerified: Boolean((raw.isVerified as boolean) ?? false),
    isActive: Boolean((raw.isActive as boolean) ?? true),
    createdAt: (raw.createdAt as string) ?? '',
    updatedAt: (raw.updatedAt as string) ?? '',
  };
}

export async function createPersonEmail(personId: string, data: PersonEmailFormData): Promise<PersonEmail> {
  const response = await apiClient.post<Record<string, unknown>>(
    `/api/v1/persons/${personId}/emails/`,
    {
      email: data.email,
      type: data.type,
      is_primary: data.isPrimary,
    },
  );
  return mapEmail(response.data);
}

export async function updatePersonEmail(personId: string, emailId: string, data: Partial<PersonEmailFormData>): Promise<PersonEmail> {
  const response = await apiClient.patch<Record<string, unknown>>(
    `/api/v1/persons/${personId}/emails/${emailId}/`,
    {
      ...(data.email !== undefined && { email: data.email }),
      ...(data.type !== undefined && { type: data.type }),
      ...(data.isPrimary !== undefined && { is_primary: data.isPrimary }),
    },
  );
  return mapEmail(response.data);
}

export async function deletePersonEmail(personId: string, emailId: string): Promise<void> {
  await apiClient.delete(`/api/v1/persons/${personId}/emails/${emailId}/`);
}

// ── Merge ──────────────────────────────────────────────────────────────────

/**
 * Merge `sourceId` into `targetId`.
 *
 * All wristbands, balances, and role records from the source are transferred
 * to the target. The source is then deactivated.
 *
 * Returns the updated target Person.
 */
export async function mergePersons(targetId: string, sourceId: string): Promise<Person> {
  const response = await apiClient.post<Record<string, unknown>>(
    `/api/v1/persons/${targetId}/merge/`,
    { source_id: sourceId },
  );
  return mapBackendPerson(response.data);
}
